import {
    type ControlledAutocompleteFormStructure,
    type ControlledTextareaFormStructure,
    type CustomItemFormStructure,
    type FlexyInputFormStructure,
    type FlexySelectFormStructure,
    type FormStructure,
    FlexyForm,
} from '@europrocurement/flexy-form';
import React, { useState } from 'react';
import { Box, Button, SxProps, Typography, useMediaQuery, useTheme } from '@mui/material';
import { type FieldValues, useForm } from 'react-hook-form';
import ContactChip from '@europrocurement/l2d-modules/modules/Dossiers/components/shared/ContactChip/ContactChip';
import { type ContactItem } from '@europrocurement/l2d-modules/modules/Dossiers';
import { type DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { type PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { type UtilisateurTaches } from '@europrocurement/l2d-domain';
import { type LiteUtilisateur } from '@europrocurement/l2d-modules/modules/Utilisateur/hooks/useUtilisateur';
import { useModal } from '@europrocurement/flexy-components';
import { TaskPriority, tasksPriorities } from '../../constant';

type OnSuccessForm = {
    taskForm: FieldValues;
    originalTask?: DossierTaches | PrescripteurTaches | UtilisateurTaches;
};

export type CreateTaskModalProps = {
    onSuccess: ({ taskForm, originalTask }: OnSuccessForm) => Promise<void>;
    onSearchUsers: (term: string) => Promise<ContactItem[]>;
    linkedEntityId?: string;
    sxProps?: SxProps;
    originalTask?: DossierTaches | PrescripteurTaches | UtilisateurTaches;
    currentUser: LiteUtilisateur;
};

type FormData = {
    title?: string;
    description?: string;
    remindAt?: Date;
    expiredAt?: Date;
    priority?: TaskPriority;
    assignTo?: ContactItem[];
};

const TaskFormModal: React.FunctionComponent<CreateTaskModalProps> = function ({
    sxProps,
    onSuccess,
    onSearchUsers,
    originalTask, // Used on update
    currentUser,
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { palette, breakpoints } = useTheme();
    const downLgBreakpoint = useMediaQuery(breakpoints.down('lg'));
    const formWidth = downLgBreakpoint ? 'calc(100vw - 200px)' : '1098px';
    const { modalActions } = useModal();

    const tache = originalTask?.tache;

    const userAssign = tache?.assignTo
        ? [
              {
                  firstName: tache.assignTo.nom,
                  id: tache.assignTo.id,
                  lastName: tache.assignTo.prenom,
              },
          ]
        : [
              {
                  firstName: currentUser.lastName,
                  id: currentUser.id,
                  lastName: currentUser.firstName,
              },
          ];

    const formContext = useForm<FormData>({
        mode: 'onTouched',
        defaultValues: {
            title: tache?.title || '',
            description: tache?.description || '',
            remindAt: tache?.remindAt ? new Date(tache.remindAt) : undefined,
            expiredAt: tache?.expiredAt ? new Date(tache.expiredAt) : undefined,
            priority: tache?.priority
                ? tasksPriorities.find((tachePrio) => tachePrio.value === tache.priority)
                : {
                      value: 0,
                      label: 'Faible',
                  },
            assignTo: userAssign,
        },
    });

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const formValues = formContext.getValues();
        await onSuccess({ taskForm: formValues, originalTask });
        setIsSubmitting(false);
    };

    const getOptionLabelUser = (option: ContactItem) => (
        <ContactChip
            sx={{ marginY: '8px' }}
            firstName={option.firstName}
            lastName={option.lastName}
        />
    );

    const formProps = {
        formContext,
        formObject: {},
        onSubmit: handleSubmit,
        gridProps: { sx: { alignItems: 'center', justifyContent: 'space-between' } },
        submitButton: {
            label: 'Envoyer',
            displayed: false,
        },
        isSubmitting,
    };

    const formStructure: FormStructure[] = [
        {
            type: 'text',
            name: 'title',
            inputlabel: 'Titre',
            rules: { required: 'Ce champ est requis' },
        },
        {
            type: 'textarea',
            name: 'description',
            inputlabel: 'Description',
            sx: {
                minHeight: '100px',
                padding: '8px 20px',
                border: `1px solid ${palette.grey['300']}`,
                borderRadius: '12px',
                ':hover': {
                    border: `1px solid ${palette.grey['900']}`,
                },
                ':focus-within': {
                    border: `1px solid ${palette.primary.main}`,
                },
                transition: 'all ease-in-out 300ms',
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
            },
        } as ControlledTextareaFormStructure,
        {
            type: 'date',
            name: 'expiredAt',
            inputlabel: "Date d'échéance",
            rules: { required: 'Ce champ est requis' },
            xs: 12,
            lg: 5.8,
        } as FlexyInputFormStructure,
        {
            type: 'date',
            name: 'remindAt',
            inputlabel: 'Rappel',
            sx: { padding: '8px' },
            xs: 12,
            lg: 5.8,
        } as FlexyInputFormStructure,
        {
            type: 'select',
            name: 'priority',
            inputlabel: 'Priorité',
            options: tasksPriorities,
            rules: {
                required: 'Ce champ est requis',
            },
            xs: 12,
            lg: 5.8,
        } as FlexySelectFormStructure,
        {
            type: 'autocomplete',
            name: 'assignTo',
            inputlabel: 'Assigner à',
            listboxTitle: 'Utilisateurs',
            placeholder: 'Assigner à',
            onSearch: onSearchUsers,
            getOptionValue: (option: ContactItem) => option.id,
            getOptionLabel: getOptionLabelUser,
            multiple: false,
            rules: {
                required: 'Utilisateur manquant',
            },
            xs: 12,
            lg: 5.8,
            sx: {
                '& .MuiOutlinedInput-root': {
                    paddingRight: '6px!important',
                },
            },
        } as ControlledAutocompleteFormStructure,
        {
            type: 'customItem',
            name: 'buttonsFooter',
            renderField: () => (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        color="primary"
                        sx={{
                            marginTop: '2rem',
                            marginBottom: '16px',
                            color: 'text.gris70',
                            padding: '16px 24px',
                            fontSize: '16px',
                        }}
                        onClick={() => modalActions.close()}
                    >
                        Annuler
                    </Button>

                    <Button
                        variant="contained"
                        type="submit"
                        sx={{
                            marginTop: '2rem',
                            marginBottom: '16px',
                            padding: '16px 24px',
                            fontSize: '16px',
                        }}
                    >
                        {originalTask ? 'Modifier' : 'Envoyer'}
                    </Button>
                </Box>
            ),
        } as CustomItemFormStructure,
    ];

    return (
        <Box sx={{ width: formWidth, ...sxProps }}>
            <Typography variant="l2dh6">{tache ? 'Modifier' : 'Ajouter'} une tâche</Typography>
            <FlexyForm
                formStructure={formStructure}
                {...formProps}
            />
        </Box>
    );
};

export default TaskFormModal;
